import { Paper } from 'design-system/Paper';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import ProtectedTextAttachment from 'react-scale/components/scalers/tasks/textcollection/ProtectedTextAttachment';
import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from 'design-system/styles';
import { CircularProgress } from 'react-scale/components/design-system';
import { useUnleashBoolean } from 'react-scale/hooks/useUnleashFeatureFlag';
import logger from '@scale/frontend-lib/logging';
import API from '@scale/frontend-lib/dashboard-api';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from 'design-system';

import DownloadableViewError from './DownloadableViewError';
interface DownloadableTextAttachmentViewProps {
  attachmentUrl: string;
  loggedUserId?: string;
  useProtectedText?: boolean;
  renderAsPlainText?: boolean;
  disableLinksInTextAttachment?: boolean;
  projectId?: string;
  showSensitiveContentWarning?: boolean;
  handleSkip?: (skipReason: string, skipExplanation: string) => void;
}

export default function DownloadableTextAttachmentView(props: DownloadableTextAttachmentViewProps) {
  const styles = useAttachmentViewStyles();
  const [content, setContent] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const { isHipaaProject: isCoveredByHipaa, isLoading: isCoveredByHipaaLoading } =
    useIsCoveredByHipaa(props.projectId);
  const [showSensitiveContentWarning, setshowSensitiveContentWarning] = React.useState(
    props.showSensitiveContentWarning,
  );

  const onProceedWarning = () => {
    setshowSensitiveContentWarning(false);
  };

  const skip = () => {
    logger.info(`[C4WarningSkip] User: ${props.loggedUserId}, Project: ${props.projectId}`);
    props.handleSkip!('Skipped because of C4 Warning', '');
  };

  const logAttachmentView = useUnleashBoolean(
    'log-downloadable-text-attachment-view',
    { projectId: props.projectId },
    {
      shortCircuit: !props.projectId,
      defaultValue: false,
    },
  );

  React.useEffect(() => {
    const fetchData = async () => {
      if (isCoveredByHipaaLoading) {
        return;
      }

      if (isCoveredByHipaa) {
        setContent(props.attachmentUrl);
        setLoading(false);
        return;
      }

      const res = await fetch(props.attachmentUrl).then(async res => {
        if (res.status === 200) {
          const text = await res.text();
          if (logAttachmentView) {
            logger.info('Downloadable text attachment URL fetched', {
              attachmentUrl: props.attachmentUrl,
              userId: props.loggedUserId,
              projectId: props.projectId,
            });
          }
          try {
            const json = JSON.parse(text);
            return json;
          } catch (err) {
            return text;
          }
        } else {
          return '[Unable to render content]';
        }
      });
      setContent(res);
      setLoading(false);
    };
    fetchData();
  }, [
    setContent,
    setLoading,
    props.attachmentUrl,
    props.loggedUserId,
    props.projectId,
    logAttachmentView,
    isCoveredByHipaa,
    isCoveredByHipaaLoading,
  ]);

  const contentAsString: string = useMemo(() => {
    return typeof content === 'object'
      ? JSON.stringify(content, null, 2)
      : content?.toString() ?? '';
  }, [content]);

  if (loading) {
    return <CircularProgress />;
  }

  if (content === null) {
    return <DownloadableViewError />;
  }

  if (isCoveredByHipaa) {
    // TODO: resolve CORS issue for HIPAA data so we can fetch the text data for rendering
    return <object className={styles.hipaaText} data={content} />;
  }

  return (
    <div className="fs-exclude">
      <Paper className={styles.textAttachment}>{renderTextComponent(props, contentAsString)}</Paper>
      {showSensitiveContentWarning && (
        <Dialog
          open={true}
          maxWidth={'xl'}
          BackdropProps={{
            classes: {
              root: styles.backDrop,
            },
          }}
        >
          <DialogTitle>Warning</DialogTitle>
          <DialogContent>
            <div style={{ width: 500 }}>
              <DialogContentText>
                The following text may contain highly sensitive content. If you do not wish to view
                this content, please skip this task. You will not be penalized should you choose to
                skip this task. If you are comfortable proceeding, please click the "Proceed" button
                below to attempt this task.
              </DialogContentText>
            </div>
          </DialogContent>
          <DialogActions>
            <Button type="primary" onClick={onProceedWarning}>
              Proceed
            </Button>
            <Button type="secondary" onClick={skip}>
              Skip
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}

const renderTextComponent = (
  props: DownloadableTextAttachmentViewProps,
  contentAsString: string,
) => {
  if (props.useProtectedText) {
    return (
      <ProtectedTextAttachment
        text={contentAsString ?? ''}
        loggedUserId={props.loggedUserId}
        renderAsPlainText={props.renderAsPlainText}
        disableLinksInTextAttachment={props.disableLinksInTextAttachment}
      />
    );
  } else if (props.renderAsPlainText) {
    return <div>{contentAsString}</div>;
  } else {
    return (
      <SanitizedMarkdown
        text={contentAsString ?? ''}
        disableLinksInTextAttachment={props.disableLinksInTextAttachment}
        addBackgroundToTransparentImages
      />
    );
  }
};

const useAttachmentViewStyles = makeStyles({
  textAttachment: {
    margin: 24,
    padding: 32,
    lineHeight: 1.6,
    '& tr': {
      border: '1px solid rgba(0, 0, 0, 0.3)',
    },
    '& tr:nth-child(even)': {
      backgroundColor: '#f2f2f2',
    },
    '& th': {
      border: '1px solid rgb(0, 0, 0, 0.3)',
      padding: '6px 10px',
      backgroundColor: '#f2f2f2',
    },
    '& td': {
      border: '1px solid rgba(0, 0, 0, 0.3)',
      padding: '10px',
    },
  },
  hipaaText: {
    height: '100%',
    width: '100%',
  },
  textAttachmentOverlay: {
    backgroundColor: '#191919',
    color: '#f2f2f2',
    margin: 24,
    padding: 32,
    lineHeight: 1.6,
  },
  buttonStyle: {
    border: 0,
    borderRadius: '8px',
    color: 'white',
    display: 'inlineBlock',
    fontSize: '12px',
    height: '36px',
    marginLeft: '12px',
    padding: '10px',
    whiteSpace: 'nowrap',
  },
  backDrop: {
    backdropFilter: 'blur(5px)',
  },
});

const useIsCoveredByHipaa = (projectId?: string) => {
  const [isHipaaProject, setIsHipaaProject] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (projectId) {
        const { isAttachmentCoveredByHipaa } =
          await API.selfserve.fetchProjectAttachmentHipaaCoverage({
            project: projectId,
          });
        if (isAttachmentCoveredByHipaa) {
          setIsHipaaProject(true);
        }
      }
      setIsLoading(false);
    };
    try {
      fetchData();
    } catch (e) {
      logger.info('Error at DownloadableTextAttachmentView useIsCoveredByHipaa');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  return { isHipaaProject, isLoading };
};
